import {useLocation} from 'react-router-dom'
import {useHeaderContext} from '../../../contexts/header-context'
import {useGlobalHeader} from '../../../contexts/global-elements-context'
import {useNavigationContext} from '../../../contexts/navigation-context'
import {HEADER_VARIATION_CMS_MAP, HEADER_GRADIENT_CMS_MAP} from '../config'

/**
 * Resolves the header variant actually in effect for the current URL.
 *
 *  - Homepage: context defaults apply unless a CMS HeaderVariations entry matches.
 *  - Other URLs: only an exact CMS UrlToApply match can enable the transparent
 *    variant; otherwise the header stays opaque (variant '3').
 *
 * Shared by the header and the desktop nav so both agree on transparency.
 */
const useEffectiveHeaderVariant = () => {
    const {headerVariant, headerGradient, headerDropShadow} = useHeaderContext()
    const {data: headerData} = useGlobalHeader()
    const {pathname} = useLocation()
    const {isHomepagePath} = useNavigationContext()

    const isHomepage = isHomepagePath(pathname)

    // Exact URL match wins anywhere; the wildcard (empty UrlToApply) is homepage-only.
    const cmsVariation = (() => {
        if (!Array.isArray(headerData?.HeaderVariations)) return null
        const specific = headerData.HeaderVariations.find(
            (v) => v.urlToApply && pathname === v.urlToApply
        )
        if (specific) return specific
        return isHomepage ? headerData.HeaderVariations.find((v) => !v.urlToApply) || null : null
    })()

    const variant = cmsVariation
        ? HEADER_VARIATION_CMS_MAP[cmsVariation.headerVariation] || '3'
        : isHomepage
        ? headerVariant
        : '3'
    const gradient = cmsVariation
        ? HEADER_GRADIENT_CMS_MAP[cmsVariation.headerGradient] || 'none'
        : isHomepage
        ? headerGradient
        : 'none'
    const dropShadow = cmsVariation
        ? cmsVariation.headerDropShadow
        : isHomepage
        ? headerDropShadow
        : false

    return {
        variant,
        gradient,
        dropShadow,
        isTransparent: variant === '1' || variant === '2',
        isWhiteVariant: variant === '1'
    }
}

export default useEffectiveHeaderVariant
